import { DateField } from '@cmsgov/ds-medicare-gov';
import * as PropTypes from 'prop-types';
import React, { useLayoutEffect, useState, useEffect } from 'react';
import { allow } from '../classes/allow';
import '../css/date.field.css';
import addLeadingZeroes from '../functions/add.leading.zeroes';
import capitalizeFirstLetter from '../functions/capitalize.first.letter';
import { isValidYear } from '../functions/is.valid.year';
import translate from '../functions/translate';
import dateFieldValue from '../objects/models/date.field.value';

const DateFields = props => {
    const [dayFieldHasBeenVisited, setDayFieldHasBeenVisited] = useState(false);
    const [errorMessagesCreated, setErrorMessagesCreated] = useState(false);
    const [monthFieldHasBeenVisited, setMonthFieldHasBeenVisited] = useState(false);
    const [yearFieldHasBeenVisited, setYearFieldHasBeenVisited] = useState(false);
   
    const eventTypes = ['onBlur', 'onChange'];
    const fieldNames = ['day', 'month', 'year'];
   
    const allFieldsAreValid = () => props.date.day !== '' && props.date.month !== '' && isValidYear(props.date.year);
    const allVisitedFieldsAreValid = () => (props.date.day !== '' || !dayFieldHasBeenVisited) && (props.date.month !== '' || !monthFieldHasBeenVisited) && (isValidYear(props.date.year) || !yearFieldHasBeenVisited);
   
    const createUpdatedDateObject = (dateObject = {}) => {
        allow.anInstanceOf(dateObject, dateFieldValue);
        dateObject.day = dateObject.day.replace(/\D/g, '');
        dateObject.month = dateObject.month.replace(/\D/g, '');
        dateObject.year = dateObject.year.replace(/\D/g, '');
        if (dateObject.day !== '') {
            const day = parseInt(dateObject.day, 10) || 0;
            if (isNaN(day) || day < 0 || day > 31)
                dateObject.day = '01';
        }
        if (dateObject.month !== '') {
            const month = parseInt(dateObject.month, 10) || 0;
            if (isNaN(month) || month < 0 || month > 12)
                dateObject.month = '01';
        }
    };
   
    const formatValues = (dateObject = {}, fieldName = '') => {
        allow.anInstanceOf(dateObject, dateFieldValue).oneOf(fieldName, fieldNames);
        if (fieldName === 'day' && dateObject.day !== '')
            dateObject.day = addLeadingZeroes(parseInt(dateObject.day, 10), 2);
        if (fieldName === 'month' && dateObject.month !== '')
            dateObject.month = addLeadingZeroes(parseInt(dateObject.month, 10), 2);
    };
   
    const invokeEventHandlersFromCaller = (updatedDateObject = {}, eventType = '') => {
        allow.anInstanceOf(updatedDateObject, dateFieldValue).oneOf(eventType, eventTypes);
        if (eventType === 'onBlur' && props.onBlur !== null)
            props.onBlur(updatedDateObject);
        if (eventType === 'onChange' && props.onChange !== null)
            props.onChange(updatedDateObject);
    };
   
    const updateDate = (dateObject = {}, eventType = '', fieldName = '') => {
        allow.anInstanceOf(dateObject, dateFieldValue).oneOf(eventType, eventTypes).oneOf(fieldName, fieldNames);
        createUpdatedDateObject(dateObject);
        if (eventType === 'onBlur') {
            formatValues(dateObject, fieldName);
            if (fieldName === 'year') {
                setYearFieldHasBeenVisited(true);
            }
        }
        invokeEventHandlersFromCaller(dateObject, eventType);
        updateValidationTrackingVariables(fieldName);
    };
   
    const updateValidationTrackingVariables = (fieldName = '') => {
        allow.oneOf(fieldName, fieldNames);
        if (fieldName === 'day' && !dayFieldHasBeenVisited)
            setDayFieldHasBeenVisited(true);
        else if (fieldName === 'month' && !monthFieldHasBeenVisited)
            setMonthFieldHasBeenVisited(true);
    };
   
    useLayoutEffect(() => {
        if (errorMessagesCreated)
            return;
        const monthField = document.querySelector('.ds-c-field--month');
        monthField.parentElement.parentElement.insertAdjacentHTML('afterend', `
         <div
            id="dateErrorMessages"
            style="display:none;"
         >
            <div
               class="ds-u-color--error"
               id="monthErrorMessage"
               style="float:left; font-size:14px; margin-right:28px; max-width:75px;"
            >${props.monthErrorMessage}</div>
            <div
               class="ds-u-color--error"
               id="dayErrorMessage"
               style="float:left; font-size:14px; margin-right:28px; max-width:75px;"
            >${props.dayErrorMessage}</div>
            <div
               class="ds-u-color--error"
               id="yearErrorMessage"
               style="float:left; font-size:14px; max-width:75px;"
            >${props.yearErrorMessage}</div>
         </div>
      `);
        setErrorMessagesCreated(true);
    }, [errorMessagesCreated, props]);

    // update error messages when changed (e.g. on language toggle)
    useEffect(() => {
        const year = props.date.year;
        const dayError = document.getElementById('dayErrorMessage');
        if (dayError) {
            dayError.textContent = props.dayErrorMessage;
        }
   
        const monthError = document.getElementById('monthErrorMessage');
        if (monthError) {
            monthError.textContent = props.monthErrorMessage;
        }
   
        const yearError = document.getElementById('yearErrorMessage');
        if (yearError) {
            if (!year) {
                yearError.textContent = props.yearErrorMessage;
            } else {
                yearError.textContent = `${translate('Error')}: ${translate('Invalid year')}`;
            }
        }
   
    }, [props.dayErrorMessage, props.monthErrorMessage, props.yearErrorMessage, props.date]);
   
    const validate = () => {
        if (!errorMessagesCreated)
            return;
        if (
            (!props.forceValidation && allVisitedFieldsAreValid())
         || (props.forceValidation && allFieldsAreValid())
        ) {
            document.getElementById('dateErrorMessages').style.display = 'none';
            return;
        }
        document.getElementById('dateErrorMessages').style.display = 'inherit';
        document.getElementById('dayErrorMessage').style.visibility = (props.forceValidation || dayFieldHasBeenVisited) && props.date.day === '' ? 'visible' : 'hidden';
        document.getElementById('monthErrorMessage').style.visibility = (props.forceValidation || monthFieldHasBeenVisited) && props.date.month === '' ? 'visible' : 'hidden';
        document.getElementById('yearErrorMessage').style.visibility = (props.forceValidation || yearFieldHasBeenVisited) && !isValidYear(props.date.year) ? 'visible' : 'hidden';
    };
   
    validate();
    return <>
        <DateField
            autoComplete={true}
            dayFieldRef={props.dayFieldRef}
            dayInvalid={(props.forceValidation || dayFieldHasBeenVisited) && props.date.day === ''}
            dayLabel={capitalizeFirstLetter(translate('day'))}
            dayValue={props.date.day}
            hint={props.hint}
            label={props.label}
            monthFieldRef={props.monthFieldRef}
            monthInvalid={(props.forceValidation || monthFieldHasBeenVisited) && props.date.month === ''}
            monthLabel={capitalizeFirstLetter(translate('month'))}
            monthValue={props.date.month}
            onBlur={(event, dateObject) => updateDate(dateObject, 'onBlur', event.currentTarget.name)}
            onChange={(event, dateObject) => updateDate(dateObject, 'onChange', event.currentTarget.name)}
            yearFieldRef={props.yearFieldRef}
            yearInvalid={(props.forceValidation || yearFieldHasBeenVisited) && !isValidYear(props.date.year)}
            yearLabel={capitalizeFirstLetter(translate('year'))}
            yearValue={props.date.year}
        />
    </>
}

DateFields.propTypes = {
    date: PropTypes.object.isRequired,
    dayErrorMessage: PropTypes.string,
    dayFieldRef: PropTypes.func,
    forceValidation: PropTypes.bool,
    hint: PropTypes.string,
    label: PropTypes.string.isRequired,
    monthErrorMessage: PropTypes.string,
    monthFieldRef: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    yearErrorMessage: PropTypes.string,
    yearFieldRef: PropTypes.func,
};

DateFields.defaultProps = {
    dayErrorMessage: 'Error: Enter a value for Day',
    forceValidation: false,
    hint: '',
    label: '',
    monthErrorMessage: 'Error: Enter a value for Month',
    onBlur: null,
    onChange: null,
    yearErrorMessage: 'Error: Enter a value for Year',
};

export default DateFields;